import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { ShieldPlus, ExternalLink, CheckCircle2, Circle, Clock, Plus, Trash2, Pencil, ScrollText, FileCheck2, Milestone } from "lucide-react";

const TYPE_COLOR = {
  hak_cipta: "bg-emerald-600",
  paten: "bg-blue-600",
  merek: "bg-[#D97706]",
  desain_industri: "bg-violet-600",
  dtlst: "bg-rose-600",
};

const emptyForm = { title: "", haki_type: "hak_cipta", description: "", author: "", application_number: "", submission_date: "", current_stage: 0, notes: "" };

export default function Haki() {
  const [types, setTypes] = useState({});
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // haki_id
  const [progressOpen, setProgressOpen] = useState(null); // {haki_id, stages}
  const [progressForm, setProgressForm] = useState({ stage_index: 0, date: "", note: "" });

  const load = async () => {
    const [t, m] = await Promise.all([http.get("/haki/types"), http.get("/haki")]);
    setTypes(t.data); setItems(m.data);
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    try {
      if (editing) {
        await http.patch(`/haki/${editing}`, form);
        toast.success("Berhasil diperbarui");
      } else {
        await http.post("/haki", form);
        toast.success("Pendaftaran HAKI baru dibuat");
      }
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const editItem = (it) => { setEditing(it.haki_id); setForm({ ...it }); setOpen(true); };
  const newItem = () => { setEditing(null); setForm(emptyForm); setOpen(true); };

  const del = async (id) => {
    if (!confirm("Hapus pendaftaran HAKI ini?")) return;
    await http.delete(`/haki/${id}`);
    toast.success("Terhapus"); load();
  };

  const addProgress = async () => {
    try {
      await http.post(`/haki/${progressOpen.haki_id}/progress`, progressForm);
      toast.success("Progress diperbarui");
      setProgressOpen(null); load();
    } catch { toast.error("Gagal"); }
  };

  // Summary stats
  const total = items.length;
  const done = items.filter(i => i.current_stage >= (types[i.haki_type]?.stages?.length - 1)).length;
  const inProg = total - done;

  return (
    <div data-testid="haki-page">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-xs uppercase tracking-widest text-[#D97706] font-semibold">HAKI</p>
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300" variant="outline">Fitur Baru</Badge>
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap mt-1">
        <div>
          <h1 className="serif text-3xl font-bold text-[#0F2922]">Pendaftaran & Riwayat HAKI</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">Kelola pendaftaran Hak Cipta, Paten, Merek, Desain Industri, & DTLST — semua progres terlacak dari draft hingga sertifikat terbit.</p>
        </div>
        <Button onClick={newItem} className="bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]" data-testid="haki-new-btn">
          <Plus size={16} className="mr-2"/> Daftarkan Baru
        </Button>
      </div>

      {/* Summary */}
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div className="kt-card p-5"><div className="w-10 h-10 rounded-lg bg-[#0F2922] text-[#FAFA96] flex items-center justify-center mb-3"><ScrollText size={18}/></div><div className="text-xs uppercase tracking-widest text-slate-500">Total Pendaftaran</div><div className="text-2xl font-bold text-[#0F2922] mt-1">{total}</div></div>
        <div className="kt-card p-5"><div className="w-10 h-10 rounded-lg bg-amber-500 text-white flex items-center justify-center mb-3"><Clock size={18}/></div><div className="text-xs uppercase tracking-widest text-slate-500">Sedang Berjalan</div><div className="text-2xl font-bold text-[#0F2922] mt-1">{inProg}</div></div>
        <div className="kt-card p-5"><div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center mb-3"><FileCheck2 size={18}/></div><div className="text-xs uppercase tracking-widest text-slate-500">Selesai / Terdaftar</div><div className="text-2xl font-bold text-[#0F2922] mt-1">{done}</div></div>
      </div>

      {/* Types guide */}
      <div className="kt-card p-5 mt-4">
        <h3 className="font-semibold text-[#0F2922]">Jenis HAKI</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {Object.entries(types).map(([k, v]) => (
            <div key={k} className="p-3 rounded-lg border border-slate-200 hover:border-[#D97706] transition">
              <div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${TYPE_COLOR[k]}`}/><b className="text-sm text-[#0F2922]">{v.label}</b></div>
              <p className="text-xs text-slate-600 mt-1">{v.desc}</p>
              <p className="text-[10px] text-slate-400 mt-1 mono">{v.stages.length} tahap</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3">Pendaftaran resmi dilakukan di <a href="https://dgip.go.id" target="_blank" rel="noreferrer" className="text-[#D97706] font-semibold inline-flex items-center gap-1">DJKI Kemenkumham <ExternalLink size={11}/></a> — halaman ini membantu Anda melacak progresnya.</p>
      </div>

      {/* List */}
      <h3 className="serif text-2xl font-bold text-[#0F2922] mt-8 mb-3">Daftar HAKI Saya</h3>
      {items.length === 0 && (
        <div className="kt-card p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#FAFA96] text-[#0F2922] mx-auto flex items-center justify-center"><ShieldPlus size={26}/></div>
          <p className="text-slate-500 mt-3">Belum ada pendaftaran. Mulai lacak karya Anda.</p>
          <Button onClick={newItem} className="mt-4 bg-[#D97706] hover:bg-[#B45309] text-white">Buat Pendaftaran Pertama</Button>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(it => {
          const stages = types[it.haki_type]?.stages || [];
          const pct = stages.length > 1 ? Math.round((it.current_stage / (stages.length - 1)) * 100) : 0;
          const isDone = it.current_stage >= stages.length - 1;
          return (
            <div key={it.haki_id} className="kt-card p-5" data-testid="haki-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${TYPE_COLOR[it.haki_type]} text-white`}>{types[it.haki_type]?.label || it.haki_type}</Badge>
                    {isDone && <Badge className="bg-emerald-600"><CheckCircle2 size={11} className="mr-1"/>Selesai</Badge>}
                  </div>
                  <h4 className="font-bold text-[#0F2922] mt-2 truncate">{it.title}</h4>
                  {it.author && <p className="text-xs text-slate-500 truncate">Pencipta: {it.author}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={() => editItem(it)} data-testid={`haki-edit-${it.haki_id}`}><Pencil size={14}/></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(it.haki_id)} data-testid={`haki-delete-${it.haki_id}`}><Trash2 size={14}/></Button>
                </div>
              </div>
              {it.application_number && <p className="text-xs mono text-slate-500 mt-1">No. Permohonan: {it.application_number}</p>}

              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-500">Progres</span>
                  <span className="font-semibold text-[#0F2922]">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div className={`h-full ${isDone ? "bg-emerald-500" : "bg-[#D97706]"} transition-all`} style={{ width: `${pct}%` }}/>
                </div>
                <div className="text-xs text-[#0F2922] font-semibold mt-2 flex items-center gap-2">
                  <Milestone size={14} className="text-[#D97706]"/>{stages[it.current_stage] || "-"}
                </div>
              </div>

              {/* Timeline steps */}
              <ol className="mt-4 space-y-2">
                {stages.map((s, idx) => {
                  const reached = idx <= it.current_stage;
                  return (
                    <li key={idx} className="flex items-start gap-2 text-xs">
                      {reached ? <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0"/> : <Circle size={14} className="text-slate-300 mt-0.5 shrink-0"/>}
                      <span className={reached ? "text-[#0F2922] font-medium" : "text-slate-400"}>{s}</span>
                    </li>
                  );
                })}
              </ol>

              {/* Timeline history */}
              {(it.timeline || []).length > 0 && (
                <Accordion type="single" collapsible className="mt-3">
                  <AccordionItem value="tl" className="border-b-0">
                    <AccordionTrigger className="text-xs py-2">Riwayat aktivitas ({(it.timeline || []).length})</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {[...(it.timeline || [])].reverse().map((t, i) => (
                          <div key={i} className="border-l-2 border-[#D97706] pl-3 py-1">
                            <div className="text-xs font-semibold text-[#0F2922]">{t.stage}</div>
                            <div className="text-[11px] text-slate-500">{t.date}</div>
                            {t.note && <div className="text-xs text-slate-600 mt-0.5">{t.note}</div>}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}

              <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => { setProgressOpen({ haki_id: it.haki_id, stages }); setProgressForm({ stage_index: Math.min(it.current_stage + 1, stages.length - 1), date: new Date().toISOString().slice(0,10), note: "" }); }} data-testid={`haki-progress-${it.haki_id}`}>
                <Plus size={14} className="mr-1"/> Tambah Progres
              </Button>
              <Link to={`/haki/${it.haki_id}`} className="block mt-2" data-testid={`haki-open-${it.haki_id}`}>
                <Button size="sm" className="w-full bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]">
                  <FileCheck2 size={14} className="mr-1"/> Konsultasi AI & Bukti File
                </Button>
              </Link>
            </div>
          );
        })}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Pendaftaran HAKI" : "Daftarkan HAKI Baru"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Judul karya / invensi</Label>
              <Input required value={form.title} onChange={e=>setF("title", e.target.value)} data-testid="haki-title-input"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jenis HAKI</Label>
                <Select value={form.haki_type} onValueChange={v=>setF("haki_type", v)}>
                  <SelectTrigger data-testid="haki-type-select"><SelectValue/></SelectTrigger>
                  <SelectContent>{Object.entries(types).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Pencipta / Pemohon</Label>
                <Input value={form.author} onChange={e=>setF("author", e.target.value)} data-testid="haki-author-input"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>No. Permohonan (opsional)</Label>
                <Input value={form.application_number} onChange={e=>setF("application_number", e.target.value)} data-testid="haki-appno-input"/>
              </div>
              <div><Label>Tanggal Pengajuan</Label>
                <Input type="date" value={form.submission_date} onChange={e=>setF("submission_date", e.target.value)}/>
              </div>
            </div>
            <div><Label>Tahap saat ini</Label>
              <Select value={String(form.current_stage)} onValueChange={v=>setF("current_stage", parseInt(v))}>
                <SelectTrigger data-testid="haki-stage-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {(types[form.haki_type]?.stages || []).map((s, i) => <SelectItem key={i} value={String(i)}>{i+1}. {s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Deskripsi singkat</Label>
              <Textarea value={form.description} onChange={e=>setF("description", e.target.value)} rows={2}/>
            </div>
            <div><Label>Catatan internal</Label>
              <Textarea value={form.notes} onChange={e=>setF("notes", e.target.value)} rows={2}/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
              <Button type="submit" className="bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]" data-testid="haki-save-btn">{editing ? "Simpan Perubahan" : "Daftarkan"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Progress dialog */}
      <Dialog open={!!progressOpen} onOpenChange={(v) => !v && setProgressOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Progres Baru</DialogTitle></DialogHeader>
          {progressOpen && (
            <div className="space-y-3">
              <div><Label>Tahap</Label>
                <Select value={String(progressForm.stage_index)} onValueChange={v=>setProgressForm({...progressForm, stage_index: parseInt(v)})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {progressOpen.stages.map((s, i) => <SelectItem key={i} value={String(i)}>{i+1}. {s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal</Label>
                <Input type="date" value={progressForm.date} onChange={e=>setProgressForm({...progressForm, date: e.target.value})}/>
              </div>
              <div><Label>Catatan</Label>
                <Textarea value={progressForm.note} onChange={e=>setProgressForm({...progressForm, note: e.target.value})} rows={2} placeholder="Contoh: Dokumen diterima DJKI, no antrian 12345"/>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setProgressOpen(null)}>Batal</Button>
                <Button onClick={addProgress} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="haki-progress-save-btn">Simpan Progres</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
